import { useState, useEffect, useRef } from "react";
import {
  X, Plus, Search, CreditCard, Calendar, IndianRupee,
  Building2, CheckCircle2, Loader2, Hash,
  ChevronDown, AlertCircle, Copy, RefreshCw, FileText,
} from "lucide-react";
import { BASE_URL } from "../../../config/api";
import axios from "axios";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const INPUT_BASE =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[13px] font-semibold text-slate-800 outline-none transition-all duration-150 placeholder:font-normal placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

const PAYMENT_MODES = ["Cash", "Cheque", "Bank Transfer", "UPI", "DD"];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
/** Generate a unique alphanumeric reference for Cash payments */
function generateCashRef(retailer) {
  const now = new Date();

  const year = now.getFullYear();

  const month = String(now.getMonth() + 1).padStart(2, "0");

  const date = String(now.getDate()).padStart(2, "0");

  const hours = String(now.getHours()).padStart(2, "0");

  const minutes = String(now.getMinutes()).padStart(2, "0");

  const seconds = String(now.getSeconds()).padStart(2, "0");

  // Vendor / Company Code
  const vendorCode =
    retailer?.company_code ||
    retailer?.code ||
    "VENDOR";

  return `CASH-${vendorCode}-${year}${month}${date}${hours}${minutes}${seconds}`;
}
// ─────────────────────────────────────────────────────────────────────────────
//  ATOM: FieldGroup
// ─────────────────────────────────────────────────────────────────────────────
function FieldGroup({ label, icon: Icon, children, error, required }) {
  return (
    <div>
      <label className="block text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
        {Icon && <Icon size={10} className="inline mr-1 opacity-70"/>}
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
      {error && (
        <p className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-red-500">
          <AlertCircle size={10}/> {error}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  ATOM: StepBadge
// ─────────────────────────────────────────────────────────────────────────────
function StepBadge({ n, done }) {
  return (
    <div
      className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-black shrink-0"
      style={{
        background: done ? "#ECFDF5" : "#EFF6FF",
        color:      done ? "#059669"  : "#2563EB",
        border:     `1.5px solid ${done ? "#A7F3D0" : "#BFDBFE"}`,
      }}
    >
      {done ? <CheckCircle2 size={12}/> : n}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MOLECULE: RetailerSearch + RetailerCard
// ─────────────────────────────────────────────────────────────────────────────
function RetailerSearch({ onSelect }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const debounce = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);

    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, []);

  const handleInput = (val) => {

    setQuery(val);

    clearTimeout(debounce.current);

    // minimum 4 letters
    if (val.trim().length < 4) {

      setResults([]);
      setOpen(false);

      return;
    }

    debounce.current = setTimeout(async () => {

      try {

        setLoading(true);

        const response = await axios.get(
          `${BASE_URL}common/get-users`,
          {
            params: {
              role: 5,
              search_value: val,
              page: 1,
            },

            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );

        const json = response.data;

        const list =
          json?.data || [];

        setResults(list);

        setOpen(
          list.length > 0
        );

      } catch (err) {

        console.error(err);

        setResults([]);

      } finally {

        setLoading(false);

      }

    }, 500);
  };

  const pick = (retailer) => {
    onSelect(retailer);

    setQuery(
      `${retailer.company_code || "-"} - ${
        retailer.business_name || ""
      }`
    );

    setResults([]);

    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Search
          size={14}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />

        {loading && (
          <Loader2
            size={13}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-500 animate-spin"
          />
        )}

        <input
          type="text"
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Search retailer (min 4 letters)"
          className={INPUT_BASE + " pl-9 pr-9"}
        />
      </div>

      {open && (
        <div
          className="absolute z-50 w-full mt-1 rounded-2xl overflow-hidden"
          style={{
            boxShadow: "0 8px 32px rgba(0,0,0,0.13)",
            border: "1px solid #E2E8F0",
            background: "#fff",
            maxHeight: "320px",
            overflowY: "auto",
          }}
        >
          {results.map((r, i) => (
            <button
              key={r.id || i}
              type="button"
              onMouseDown={() => pick(r)}
              className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-blue-50 transition-colors border-b border-slate-50 last:border-0"
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-white text-[12px] shrink-0"
                style={{
                  background:
                    "linear-gradient(135deg,#2563EB,#1D4ED8)",
                }}
              >
                {(r.business_name || "R")[0].toUpperCase()}
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-bold text-slate-800 leading-tight">
                  {r.company_code || "-"} - {r.business_name}
                </p>

                <p className="text-[10px] text-slate-500 mt-1">
                  {r.contact_person}
                </p>

                <p className="text-[10px] text-slate-400">
                  {r.city}, {r.state}
                </p>
              </div>
            </button>
          ))}

          {!loading && results.length === 0 && (
            <div className="px-4 py-6 text-center text-[12px] text-slate-400">
              No retailer found
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RetailerCard({ retailer, onClear }) {
  return (
    <div
      className="flex items-center gap-3 rounded-xl p-3.5 mt-1"
      style={{ background:"#EFF6FF", border:"1px solid #BFDBFE" }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-white shrink-0 text-[13px]"
        style={{ background:"linear-gradient(135deg,#2563EB,#1D4ED8)" }}
      >
        {(retailer.business_name||"R")[0].toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-bold text-slate-800 truncate">{retailer.business_name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span
            className="text-[10px] font-black px-2 py-0.5 rounded-full"
            style={{ background:"#DBEAFE", color:"#1D4ED8" }}
          >
            {retailer.company_code || retailer.code || "—"}
          </span>
          {retailer.city && (
            <span className="text-[10px] text-slate-400">{retailer.city}</span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onClear}
        className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center hover:bg-red-100 transition-colors"
        style={{ background:"#FEE2E2" }}
      >
        <X size={12} className="text-red-500"/>
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MOLECULE: PaymentModeSelector
// ─────────────────────────────────────────────────────────────────────────────
function PaymentModeSelector({ value, onChange }) {
  const modes = [
    { id:"Cash",         icon:"💵", color:"#059669", bg:"#ECFDF5", border:"#A7F3D0" },
    { id:"Cheque",       icon:"📄", color:"#7C3AED", bg:"#F5F3FF", border:"#DDD6FE" },
    // { id:"Bank Transfer",icon:"🏦", color:"#2563EB", bg:"#EFF6FF", border:"#BFDBFE" },
    // { id:"UPI",          icon:"📱", color:"#D97706", bg:"#FFFBEB", border:"#FDE68A" },
    // { id:"DD",           icon:"📋", color:"#0891B2", bg:"#ECFEFF", border:"#A5F3FC" },
  ];

  return (
    <div className="grid grid-cols-5 gap-2">
      {modes.map(m => {
        const active = value === m.id;
        return (
          <button
            key={m.id}
            type="button"
            onClick={() => onChange(m.id)}
            className="flex flex-col items-center gap-1.5 py-2.5 px-1 rounded-xl text-center transition-all duration-150 active:scale-95"
            style={{
              background:  active ? m.bg     : "#F8FAFC",
              border:      `2px solid ${active ? m.border : "#E2E8F0"}`,
              boxShadow:   active ? `0 4px 12px ${m.color}22` : "none",
            }}
          >
            <span className="text-[18px] leading-none">{m.icon}</span>
            <span
              className="text-[9px] font-black uppercase tracking-wide leading-tight"
              style={{ color: active ? m.color : "#94A3B8" }}
            >
              {m.id}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MOLECULE: CashRefField  — auto-generated ref with copy + regenerate
// ─────────────────────────────────────────────────────────────────────────────
function CashRefField({ value, onChange, selectedRetailer }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        readOnly
        className={INPUT_BASE + " pr-20 bg-slate-50 font-mono text-[12px]"}
        style={{ color:"#059669" }}
      />
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
        <button
          type="button"
          onClick={() => onChange(generateCashRef(selectedRetailer))}
          title="Regenerate"
          className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-slate-100 transition-colors"
        >
          <RefreshCw size={12} className="text-slate-400"/>
        </button>
        <button
          type="button"
          onClick={copy}
          title="Copy"
          className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-slate-100 transition-colors"
        >
          {copied
            ? <CheckCircle2 size={12} className="text-green-500"/>
            : <Copy size={12} className="text-slate-400"/>
          }
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  ROOT: ManualPaymentModal
// ─────────────────────────────────────────────────────────────────────────────
function ManualPaymentModal({ onClose, onSuccess, project = "warranty" }) {
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [paymentMode,      setPaymentMode]      = useState("");
  const [paymentRef,       setPaymentRef]       = useState("");
  const [paymentDate,      setPaymentDate]      = useState(new Date().toISOString().slice(0, 10));
  const [amount,           setAmount]           = useState("");
  const [errors,           setErrors]           = useState({});
  const [saving,           setSaving]           = useState(false);
  const [success,          setSuccess]          = useState(false);

  const [bankAccounts, setBankAccounts] = useState([]);
const [selectedAccountId, setSelectedAccountId] = useState("");

  const [bankName, setBankName] = useState("");
const [paymentNote, setPaymentNote] = useState("");
const [paymentImage, setPaymentImage] = useState(null);
const [previewImage, setPreviewImage] = useState("");
const [uploadLoading, setUploadLoading] = useState(false);


// const handleImageUpload = (e) => {
//   const file = e.target.files[0];

//   if (!file) return;

//   setPaymentImage(file);

//   const imageUrl = URL.createObjectURL(file);

//   setPreviewImage(imageUrl);
// };

 const handleImageUpload = async (e) => {

    const file =
      e.target.files[0];

    if (!file) return;

    setUploadLoading(true);

    const formData =
      new FormData();

    formData.append(
      "file",
      file
    );

    formData.append(
      "tag",
      "advance_payment"
    );

    try {

      const response =
        await axios.post(
          `${BASE_URL}warranty/upload-file`,
          formData,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",

              Accept:
                "application/json",
            },
          }
        );

      const data =
        response.data;

      if (
        data?.data?.file_url
      ) {

        setPreviewImage(
          data.data.file_url
        );

      }

    } catch (err) {

      console.error(err);

      alert(
        "Image upload failed"
      );

    } finally {

      setUploadLoading(false);

    }
  };


  useEffect(() => {

  async function fetchBankAccounts() {

    try {

      const response = await axios.get(
        `${BASE_URL}warranty/bank-accounts`
      );

      if (response.data?.status) {

        setBankAccounts(
          response.data.data || []
        );

      }

    } catch (err) {

      console.error(
        "Failed to fetch bank accounts",
        err
      );

    }
  }

  fetchBankAccounts();

}, []);
  // When mode changes to Cash → auto-generate ref
  // When mode changes to non-Cash → clear the generated ref
  useEffect(() => {
    if (paymentMode === "Cash" && selectedRetailer) {
    setPaymentRef(generateCashRef(selectedRetailer));
    } else {
      setPaymentRef("");
    }
    setErrors(e => ({ ...e, payment_mode:"", payment_ref:"" }));
  }, [paymentMode, selectedRetailer]);

  const isCash    = paymentMode === "Cash";
  const isCheque  = paymentMode === "Cheque";
  const isDDOrRef = ["DD", "Bank Transfer", "UPI"].includes(paymentMode);

  const refLabel = isCash
    ? "Generated Reference"
    : isCheque
    ? "Cheque Number"
    : isDDOrRef
    ? "Reference / UTR Number"
    : "Payment Reference";

  const refPlaceholder = isCash
    ? ""
    : isCheque
    ? "Enter cheque number"
    : isDDOrRef
    ? "Enter UTR / reference number"
    : "Enter reference";

  const clearErr = (key) => setErrors(e => ({ ...e, [key]:"" }));

  const validate = () => {
    const e = {};
    if (!selectedRetailer)                    e.retailer     = "Select a retailer";
    if (!paymentMode)                         e.payment_mode = "Select a payment mode";
    if (!paymentRef.trim())                   e.payment_ref  = "Required";
    if (!paymentDate)                         e.payment_date = "Required";
    if (!amount || isNaN(+amount) || +amount <= 0) e.amount  = "Enter a valid amount";
    return e;
  };

  const handleSubmit = async (ev) => {

    ev.preventDefault();

    const errs = validate();

    if (
      Object.keys(errs).length
    ) {

      setErrors(errs);

      return;
    }

    try {

      setSaving(true);

      // payment mode mapping
      const paymentModeMap = {
        Cash: "Cash WM",
        Cheque: "Cheque WM",
      };

      const payload = {
        company_id:
          selectedRetailer?.company_id,

        retailer_id:
          selectedRetailer?.id,

        payment_id:
          paymentRef,

        payment_mode:
          paymentModeMap[
            paymentMode
          ] || paymentMode,

        amount:
          Number(amount),

        url:
          previewImage || "",

        description:
          paymentNote || "",

        account_id:
          selectedAccountId || "",
      };

      const response =
        await axios.post(
          `${BASE_URL}warranty/add-wallet-balance`,
          payload,
          {
            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );

      const json =
        response.data;

      setSuccess(true);

      setTimeout(() => {

        onSuccess?.();

        onClose();

      }, 1500);

    } catch (err) {

      setErrors({
        submit:
          err?.response?.data
            ?.message ||
          err?.message ||
          "Failed to save payment",
      });

    } finally {

      setSaving(false);

    }
  };

  // Steps completion state for step badges
  const s1done = !!selectedRetailer;
  const s2done = !!paymentMode;
  const s3done = !!paymentRef.trim();
  const s4done = !!paymentDate;
  const s5done = !!amount && !isNaN(+amount) && +amount > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background:"rgba(15,23,42,0.6)", backdropFilter:"blur(5px)" }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div
        className="relative w-full max-w-2xl rounded-3xl overflow-hidden"
        style={{ background:"#fff", boxShadow:"0 28px 72px rgba(0,0,0,0.2)", maxHeight:"94vh", overflowY:"auto" }}
      >

        {/* ── HEADER ── */}
        <div
          className="flex items-center justify-between px-6 py-5 sticky top-0 z-10"
          style={{ background:"linear-gradient(135deg,#1E3A8A,#2563EB)" }}
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background:"rgba(255,255,255,0.15)" }}>
              <CreditCard size={17} color="white"/>
            </div>
            <div>
              <h2 className="text-white font-black text-[15px] leading-tight">Add Manual Payment</h2>
              <p className="text-blue-200 text-[11px] font-medium mt-0.5">
                Cash · Cheque · Bank Transfer · UPI · DD
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl flex items-center justify-center transition-colors hover:bg-white/20"
          >
            <X size={15} color="white"/>
          </button>
        </div>

        {/* ── SUCCESS ── */}
        {success && (
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
              style={{ background:"#ECFDF5" }}>
              <CheckCircle2 size={32} style={{ color:"#10B981" }}/>
            </div>
            <p className="font-black text-slate-800 text-[18px]">Payment Recorded!</p>
            <p className="text-slate-400 text-[12px] mt-1">Manual payment saved successfully.</p>
          </div>
        )}

        {/* ── FORM ── */}
        {!success && (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">

            {/* ── STEP 1: Retailer ── */}
            <div
              className="rounded-2xl p-4 space-y-3"
              style={{ background:"#F8FAFC", border:"1px solid #F1F5F9" }}
            >
              <div className="flex items-center gap-2">
                {/* <StepBadge n="1" done={s1done}/> */}
                <span className="text-[12px] font-black uppercase tracking-widest text-slate-500">
                  Select Retailer
                </span>
              </div>
              {selectedRetailer
                ? <RetailerCard retailer={selectedRetailer} onClear={() => setSelectedRetailer(null)}/>
                : <RetailerSearch onSelect={r => { setSelectedRetailer(r); clearErr("retailer"); }}/>
              }
              {errors.retailer && (
                <p className="flex items-center gap-1 text-[11px] font-semibold text-red-500 mt-1">
                  <AlertCircle size={10}/> {errors.retailer}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
                {/* ── STEP 2: Payment Mode ── */}
            <div
              className="rounded-2xl p-4 space-y-3"
              style={{ background:"#F8FAFC", border:"1px solid #F1F5F9" }}
            >
              <div className="flex items-center gap-2">
                {/* <StepBadge n="2" done={s2done}/> */}
                <span className="text-[12px] font-black uppercase tracking-widest text-slate-500">
                  Payment Mode
                </span>
              </div>
              <PaymentModeSelector value={paymentMode} onChange={setPaymentMode}/>
              {errors.payment_mode && (
                <p className="flex items-center gap-1 text-[11px] font-semibold text-red-500">
                  <AlertCircle size={10}/> {errors.payment_mode}
                </p>
              )}
            </div>

            {/* ── STEP 3: Reference ── */}
            <div
              className="rounded-2xl p-4 space-y-3"
              style={{ background:"#F8FAFC", border:"1px solid #F1F5F9" }}
            >
              <div className="flex items-center gap-2">
                {/* <StepBadge n="3" done={s3done}/> */}
                <span className="text-[12px] font-black uppercase tracking-widest text-slate-500">
                  {refLabel}
                </span>
                {isCash && (
                  <span
                    className="text-[9px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full ml-auto"
                    style={{ background:"#ECFDF5", color:"#059669" }}
                  >
                    Auto
                  </span>
                )}
              </div>

              {isCash
                ? <CashRefField
                    value={paymentRef}
                    onChange={setPaymentRef}
                    selectedRetailer={selectedRetailer}
                    />
                : (
                  <div className="relative">
                    <FileText size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                    <input
                      type="text"
                      value={paymentRef}
                      onChange={e => { setPaymentRef(e.target.value); clearErr("payment_ref"); }}
                      placeholder={paymentMode ? refPlaceholder : "Select a payment mode first"}
                      disabled={!paymentMode}
                      className={INPUT_BASE + " pl-9"}
                    />
                  </div>
                )
              }
              {errors.payment_ref && (
                <p className="flex items-center gap-1 text-[11px] font-semibold text-red-500">
                  <AlertCircle size={10}/> {errors.payment_ref}
                </p>
              )}
            </div>
            </div>

            {paymentMode !== "Cash" && (
                <div className="grid grid-cols-2 gap-3">
                {/* ── BANK NAME ── */}
            <div
            className="rounded-2xl p-4 space-y-2"
            style={{ background:"#F8FAFC", border:"1px solid #F1F5F9" }}
            >
            <div className="flex items-center gap-2">
                <Building2 size={13} className="text-slate-400" />
                <span className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                Bank Name
                </span>
            </div>

            <input
                type="text"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                placeholder="Enter bank name"
                className={INPUT_BASE}
            />
            </div>

            {/* ── UPLOAD PAYMENT IMAGE ── */}
            <div
            className="rounded-2xl p-4 space-y-3"
            style={{ background:"#F8FAFC", border:"1px solid #F1F5F9" }}
            >
            <div className="flex items-center gap-2">
                <FileText size={13} className="text-slate-400" />
                <span className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                Upload Payment Proof
                </span>
            </div>

            <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className={INPUT_BASE}
            />

            {previewImage && (
                <div className="relative mt-2">
                <img
                    src={previewImage}
                    alt="Preview"
                    className="w-full h-44 object-cover rounded-xl border border-slate-200"
                />

                <button
                    type="button"
                    onClick={() => {
                    setPaymentImage(null);
                    setPreviewImage("");
                    }}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center"
                >
                    <X size={14} />
                </button>
                </div>
            )}
            </div>

                </div>
            )}
            

            {/* ── STEP 4 + 5: Date + Amount ── */}
            <div className="grid grid-cols-2 gap-3">

              {/* Date */}
              <div
                className="rounded-2xl p-4 space-y-2"
                style={{ background:"#F8FAFC", border:"1px solid #F1F5F9" }}
              >
                <div className="flex items-center gap-2">
                  {/* <StepBadge n="4" done={s4done}/> */}
                  <span className="text-[11px] font-black uppercase tracking-widest text-slate-500">Date</span>
                </div>
                <div className="relative">
                  <Calendar size={13} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                  <input
                    type="date"
                    value={paymentDate}
                    onChange={e => { setPaymentDate(e.target.value); clearErr("payment_date"); }}
                    className={INPUT_BASE + " pl-9 text-[12px]"}
                  />
                </div>
                {errors.payment_date && (
                  <p className="flex items-center gap-1 text-[10px] font-semibold text-red-500">
                    <AlertCircle size={9}/> {errors.payment_date}
                  </p>
                )}
              </div>

              {/* Amount */}
              <div
                className="rounded-2xl p-4 space-y-2"
                style={{ background:"#F8FAFC", border:"1px solid #F1F5F9" }}
              >
                <div className="flex items-center gap-2">
                  {/* <StepBadge n="5" done={s5done}/> */}
                  <span className="text-[11px] font-black uppercase tracking-widest text-slate-500">Amount</span>
                </div>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-[13px]">₹</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={amount}
                    onChange={e => { setAmount(e.target.value); clearErr("amount"); }}
                    placeholder="0.00"
                    className={INPUT_BASE + " pl-8 text-[13px]"}
                  />
                </div>
                {errors.amount && (
                  <p className="flex items-center gap-1 text-[10px] font-semibold text-red-500">
                    <AlertCircle size={9}/> {errors.amount}
                  </p>
                )}
              </div>
            </div>

            {/* ── BANK ACCOUNT ── */}
            <div
              className="rounded-2xl p-4 space-y-2"
              style={{
                background:"#F8FAFC",
                border:"1px solid #F1F5F9"
              }}
            >

              <div className="flex items-center gap-2">
                <Building2
                  size={13}
                  className="text-slate-400"
                />

                <span className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                  Select Account
                </span>
              </div>

              <div className="relative">

                <select
                  value={selectedAccountId}
                  onChange={(e) =>
                    setSelectedAccountId(
                      e.target.value
                    )
                  }
                  className={INPUT_BASE + " appearance-none"}
                >

                  <option value="">
                    Select Bank Account
                  </option>

                  {bankAccounts.map(
                    (account) => (
                      <option
                        key={account.id}
                        value={
                          account.account_id
                        }
                      >
                        {account.account_name}
                      </option>
                    )
                  )}

                </select>

                <ChevronDown
                  size={14}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />

              </div>

            </div>


            {/* ── PAYMENT NOTE ── */}
            <div
            className="rounded-2xl p-4 space-y-2"
            style={{ background:"#F8FAFC", border:"1px solid #F1F5F9" }}
            >
            <div className="flex items-center gap-2">
                <FileText size={13} className="text-slate-400" />
                <span className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                Payment Note
                </span>
            </div>

            <textarea
                rows={4}
                value={paymentNote}
                onChange={(e) => setPaymentNote(e.target.value)}
                placeholder="Enter note related to payment..."
                className={INPUT_BASE + " resize-none"}
            />
            </div>

            

            {/* ── SUMMARY STRIP ── */}
            {selectedRetailer && paymentMode && amount && (
              <div
                className="rounded-2xl px-4 py-3 flex items-center justify-between"
                style={{ background:"#EFF6FF", border:"1px solid #BFDBFE" }}
              >
                <div className="flex items-center gap-2">
                  <Building2 size={13} style={{ color:"#2563EB" }}/>
                  <span className="text-[12px] font-bold text-slate-700 truncate max-w-[160px]">
                    {selectedRetailer.business_name}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className="text-[10px] font-black px-2 py-0.5 rounded-full"
                    style={{ background:"#DBEAFE", color:"#1D4ED8" }}
                  >
                    {paymentMode}
                  </span>
                  <span className="text-[14px] font-black" style={{ color:"#1D4ED8" }}>
                    ₹{Number(amount || 0).toLocaleString("en-IN")}
                  </span>
                </div>
              </div>
            )}

            {/* Submit error */}
            {errors.submit && (
              <div className="flex items-center gap-2 rounded-xl px-3 py-2.5"
                style={{ background:"#FEF2F2", border:"1px solid #FECACA" }}>
                <AlertCircle size={13} className="text-red-500 shrink-0"/>
                <p className="text-[12px] font-semibold text-red-600">{errors.submit}</p>
              </div>
            )}

            {/* ── ACTIONS ── */}
            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl text-[13px] font-bold border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-[13px] font-black text-white transition-all active:scale-95"
                style={{
                  background:  saving ? "#94A3B8" : "linear-gradient(135deg,#2563EB,#1D4ED8)",
                  boxShadow:   saving ? "none" : "0 6px 20px rgba(37,99,235,0.35)",
                  cursor:      saving ? "not-allowed" : "pointer",
                }}
              >
                {saving
                  ? <><Loader2 size={13} className="animate-spin"/> Saving…</>
                  : <><CheckCircle2 size={13}/> Record Payment</>
                }
              </button>
            </div>

          </form>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  EXPORT: AddManualPaymentButton
// ─────────────────────────────────────────────────────────────────────────────
export function AddManualPaymentButton({ onSuccess, project }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-black text-white transition-all hover:scale-105 active:scale-95"
        style={{
          background:"linear-gradient(135deg,#2563EB,#1D4ED8)",
          boxShadow: "0 4px 14px rgba(37,99,235,0.35)",
        }}
      >
        <Plus size={14}/> Add
      </button>
      {open && (
        <ManualPaymentModal
          onClose={() => setOpen(false)}
          onSuccess={onSuccess}
          project={project}
        />
      )}
    </>
  );
}

export default ManualPaymentModal;